import {
  FOCUS_RING_CONTRAST_FLOOR,
  FOCUS_RING_WIDTH_PX,
  RING_INK_DARK,
  RING_INK_LIGHT,
  RING_PALETTE_SLOTS,
  chooseRingInk,
  contrastRatio,
  focusRing,
  focusRingColors,
  focusRingInkDecisions,
  focusVisibleComponents,
  relativeLuminance
} from "./chunk-GOUF5BMY.mjs";

// src/components/StyledSwitch.tsx
import { styled, Switch } from "@mui/material";
var StyledSwitch = styled(Switch)(({ theme }) => ({
  width: 48,
  height: 24,
  padding: 0,
  display: "flex",
  transition: "0.2s all ease-in-out",
  "& .MuiSwitch-switchBase": {
    padding: 4,
    color: theme.palette.text.disabled,
    "&.Mui-checked": {
      transform: "translateX(24px)",
      color: theme.palette.primary.contrastText,
      "& + .MuiSwitch-track": {
        opacity: 1,
        outline: "none",
        outlineOffset: 0,
        backgroundColor: theme.palette.primary.main
      }
    },
    "&.Mui-disabled": {
      color: theme.palette.grey[400],
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.grey[300],
        outline: "none",
        opacity: 1
      }
    },
    "&.Mui-disabled.Mui-checked": {
      color: theme.palette.grey[400],
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.grey[300],
        outline: "none",
        opacity: 1
      }
    }
  },
  "& .MuiSwitch-thumb": {
    width: 16,
    height: 16,
    boxShadow: "none"
  },
  "& .MuiSwitch-track": {
    padding: 0,
    border: "none",
    borderRadius: 24,
    outline: `1px solid ${theme.palette.divider}`,
    outlineOffset: -1,
    backgroundColor: "transparent",
    opacity: 1
  }
}));
var StyledSwitch_default = StyledSwitch;

// src/components/SubOption.tsx
import { Typography, useTheme } from "@mui/material";
import { jsx, jsxs } from "react/jsx-runtime";
function SubOption({ title, description, inline = false, contained = true, children }) {
  const theme = useTheme();
  const content = inline ? /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: "32px" }, children: [
    /* @__PURE__ */ jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [
      /* @__PURE__ */ jsx(Typography, { fontSize: 14, fontWeight: 600, children: title }),
      description && /* @__PURE__ */ jsx(Typography, { fontSize: 13, color: "text.secondary", sx: { mt: 0.5 }, children: description })
    ] }),
    /* @__PURE__ */ jsx("div", { style: { flexShrink: 0 }, children })
  ] }) : /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(Typography, { fontSize: 14, fontWeight: 600, gutterBottom: true, children: title }),
    description && /* @__PURE__ */ jsx(Typography, { fontSize: 13, color: "text.secondary", sx: { mb: 1.5 }, children: description }),
    children
  ] });
  if (!contained) return content;
  return /* @__PURE__ */ jsx("div", { style: {
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: "8px",
    padding: "16px",
    backgroundColor: "transparent"
  }, children: content });
}

// src/components/KeyValueEditor.tsx
import { useState, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography as Typography2,
  Tooltip,
  useTheme as useTheme2
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
function KeyValueEditor({
  value,
  onChange,
  disabled,
  knownFields = [],
  allowCustomFields = true,
  placeholder = "Enter value..."
}) {
  const theme = useTheme2();
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const knownFieldKeys = knownFields.map((f) => f.key);
  const customFields = Object.entries(value).filter(([key]) => !knownFieldKeys.includes(key));
  const handleKnownFieldChange = useCallback(
    (key, newFieldValue) => {
      onChange({
        ...value,
        [key]: newFieldValue
      });
    },
    [onChange, value]
  );
  const handleKnownFieldBlur = useCallback(
    (key, newFieldValue) => {
      if (value[key] !== newFieldValue) {
        onChange({
          ...value,
          [key]: newFieldValue
        });
      }
    },
    [onChange, value]
  );
  const handleCustomFieldChange = useCallback(
    (key, newFieldValue) => {
      onChange({
        ...value,
        [key]: newFieldValue
      });
    },
    [onChange, value]
  );
  const handleDeleteField = useCallback(
    (key) => {
      const newValue2 = { ...value };
      delete newValue2[key];
      onChange(newValue2);
    },
    [onChange, value]
  );
  const handleAddField = useCallback(() => {
    if (!newKey.trim()) return;
    const formattedKey = newKey.trim().toLowerCase().replace(/\s+/g, "_");
    if (value[formattedKey] !== void 0) return;
    onChange({
      ...value,
      [formattedKey]: newValue
    });
    setNewKey("");
    setNewValue("");
  }, [newKey, newValue, onChange, value]);
  const renderKnownField = (field) => {
    const fieldValue = value[field.key];
    if (field.type === "boolean") {
      return /* @__PURE__ */ jsx2(SubOption, { inline: true, title: field.label, description: field.description, children: /* @__PURE__ */ jsx2(
        StyledSwitch_default,
        {
          checked: Boolean(fieldValue),
          onChange: (e) => handleKnownFieldChange(field.key, e.target.checked),
          disabled
        }
      ) }, field.key);
    }
    if (field.type === "number") {
      return /* @__PURE__ */ jsx2(SubOption, { inline: true, title: field.label, description: field.description, children: /* @__PURE__ */ jsx2(
        KnownNumberField,
        {
          field,
          value: fieldValue,
          onChange: (val) => handleKnownFieldBlur(field.key, val),
          disabled
        }
      ) }, field.key);
    }
    return /* @__PURE__ */ jsx2(SubOption, { inline: true, title: field.label, description: field.description, children: /* @__PURE__ */ jsx2(
      KnownStringField,
      {
        field,
        value: fieldValue,
        onChange: (val) => handleKnownFieldBlur(field.key, val),
        disabled,
        placeholder
      }
    ) }, field.key);
  };
  return /* @__PURE__ */ jsxs2("div", { style: { display: "flex", flexDirection: "column", gap: "16px" }, children: [
    knownFields.length > 0 && knownFields.map((field) => renderKnownField(field)),
    allowCustomFields && /* @__PURE__ */ jsxs2(
      "div",
      {
        style: {
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: "8px",
          padding: "16px",
          backgroundColor: theme.palette.background.paper
        },
        children: [
          /* @__PURE__ */ jsx2(Typography2, { fontSize: 14, fontWeight: 500, gutterBottom: true, children: knownFields.length > 0 ? "Additional Custom Fields" : "Custom Fields" }),
          /* @__PURE__ */ jsx2(Typography2, { fontSize: 13, color: "text.secondary", sx: { mb: 2 }, children: "Add custom key-value pairs. Keys are auto-formatted: lowercase with spaces converted to underscores." }),
          customFields.length > 0 && /* @__PURE__ */ jsx2(Box, { sx: { mb: 1.5 }, children: customFields.map(([key, fieldValue]) => /* @__PURE__ */ jsxs2(
            Box,
            {
              sx: {
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 1,
                borderBottom: `1px solid ${theme.palette.divider}`,
                "&:last-child": { borderBottom: "none" }
              },
              children: [
                /* @__PURE__ */ jsx2(
                  Typography2,
                  {
                    sx: {
                      flex: "0 0 40%",
                      fontSize: "0.875rem",
                      fontFamily: "monospace",
                      color: "text.secondary",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    },
                    children: key
                  }
                ),
                /* @__PURE__ */ jsx2(
                  TextField,
                  {
                    value: String(fieldValue ?? ""),
                    onChange: (e) => handleCustomFieldChange(key, e.target.value),
                    disabled,
                    size: "small",
                    placeholder,
                    sx: { flex: "0 0 50%" }
                  }
                ),
                /* @__PURE__ */ jsx2(Tooltip, { title: "Delete field", children: /* @__PURE__ */ jsx2(
                  IconButton,
                  {
                    onClick: () => handleDeleteField(key),
                    disabled,
                    size: "small",
                    color: "error",
                    children: /* @__PURE__ */ jsx2(DeleteIcon, { fontSize: "small" })
                  }
                ) })
              ]
            },
            key
          )) }),
          /* @__PURE__ */ jsxs2(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5, pt: customFields.length > 0 ? 1 : 0 }, children: [
            /* @__PURE__ */ jsx2(
              TextField,
              {
                value: newKey,
                onChange: (e) => setNewKey(e.target.value),
                disabled,
                size: "small",
                label: "New Key",
                placeholder: "field_name",
                sx: { flex: "0 0 40%" }
              }
            ),
            /* @__PURE__ */ jsx2(
              TextField,
              {
                value: newValue,
                onChange: (e) => setNewValue(e.target.value),
                disabled,
                size: "small",
                label: "New Value",
                placeholder,
                sx: { flex: "0 0 50%" },
                onKeyDown: (e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddField();
                  }
                }
              }
            ),
            /* @__PURE__ */ jsx2(Tooltip, { title: "Add field", children: /* @__PURE__ */ jsx2("span", { children: /* @__PURE__ */ jsx2(
              IconButton,
              {
                onClick: handleAddField,
                disabled: disabled || !newKey.trim(),
                size: "small",
                color: "primary",
                children: /* @__PURE__ */ jsx2(AddIcon, { fontSize: "small" })
              }
            ) }) })
          ] })
        ]
      }
    )
  ] });
}
function KnownStringField({
  value,
  onChange,
  disabled,
  placeholder
}) {
  const [localValue, setLocalValue] = useState(value ?? "");
  useEffect(() => {
    setLocalValue(value ?? "");
  }, [value]);
  const handleChange = (e) => {
    setLocalValue(e.target.value);
  };
  const handleBlur = () => {
    if (localValue !== (value ?? "")) {
      onChange(localValue);
    }
  };
  return /* @__PURE__ */ jsx2(
    TextField,
    {
      value: localValue,
      onChange: handleChange,
      onBlur: handleBlur,
      disabled,
      size: "small",
      placeholder,
      sx: { width: 200 }
    }
  );
}
function KnownNumberField({
  value,
  onChange,
  disabled
}) {
  const [localValue, setLocalValue] = useState(value ?? 0);
  useEffect(() => {
    setLocalValue(value ?? 0);
  }, [value]);
  const handleChange = (e) => {
    setLocalValue(Number(e.target.value) || 0);
  };
  const handleBlur = () => {
    if (localValue !== (value ?? 0)) {
      onChange(localValue);
    }
  };
  return /* @__PURE__ */ jsx2(
    TextField,
    {
      type: "number",
      value: localValue,
      onChange: handleChange,
      onBlur: handleBlur,
      disabled,
      size: "small",
      sx: { width: 120 }
    }
  );
}

// src/components/BigButtonGroup.tsx
import { Box as Box2, Grid } from "@mui/material";
import { jsx as jsx3 } from "react/jsx-runtime";
var BigButtonGroup = ({
  items,
  selectedId,
  onSelectionChange,
  columns = { xs: 12, sm: 6, md: 4, lg: 3 },
  spacing = 2,
  disabled = false,
  size = "medium"
}) => {
  const handleButtonClick = (id) => {
    if (disabled) return;
    onSelectionChange(id);
  };
  return /* @__PURE__ */ jsx3(Box2, { children: /* @__PURE__ */ jsx3(Grid, { container: true, spacing, children: items.map((item) => /* @__PURE__ */ jsx3(Grid, { size: columns, children: item.render({
    id: item.id,
    selected: selectedId === item.id,
    onClick: () => handleButtonClick(item.id),
    disabled,
    size
  }) }, item.id)) }) });
};

// src/components/SecretField.tsx
import { useState as useState2, useCallback as useCallback2 } from "react";
import {
  TextField as TextField2,
  InputAdornment,
  IconButton as IconButton2,
  CircularProgress,
  Tooltip as Tooltip2,
  Alert,
  Box as Box3
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
  Warning as WarningIcon
} from "@mui/icons-material";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var DEFAULT_MASK = "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022";
function SecretField({
  value,
  isEncrypted,
  maskValue,
  onReveal,
  onCommit,
  disabled,
  placeholder,
  helperText
}) {
  const [showValue, setShowValue] = useState2(false);
  const [revealedValue, setRevealedValue] = useState2(null);
  const [localValue, setLocalValue] = useState2("");
  const [isRevealing, setIsRevealing] = useState2(false);
  const [isCommitting, setIsCommitting] = useState2(false);
  const [error, setError] = useState2(null);
  let displayValue;
  if (showValue && revealedValue !== null) {
    displayValue = localValue || revealedValue;
  } else if (isEncrypted) {
    displayValue = maskValue ? maskValue(value) : DEFAULT_MASK;
  } else {
    displayValue = value;
  }
  const handleToggleVisibility = useCallback2(async () => {
    if (showValue) {
      setShowValue(false);
      setRevealedValue(null);
      setLocalValue("");
      return;
    }
    if (isEncrypted && value && onReveal) {
      setIsRevealing(true);
      setError(null);
      try {
        const revealed = await onReveal();
        setRevealedValue(revealed);
        setLocalValue(revealed);
        setShowValue(true);
      } catch (err) {
        console.error("Failed to reveal secret:", err);
        setError(`Failed to reveal: ${err instanceof Error ? err.message : "Unknown error"}`);
      } finally {
        setIsRevealing(false);
      }
    } else {
      setLocalValue(value);
      setShowValue(true);
    }
  }, [showValue, isEncrypted, value, onReveal]);
  const handleChange = useCallback2(
    (e) => {
      const next = e.target.value;
      setLocalValue(next);
      setError(null);
      if (showValue && revealedValue !== null) {
        setRevealedValue(next);
      }
    },
    [showValue, revealedValue]
  );
  const handleBlur = useCallback2(async () => {
    if (disabled) return;
    const currentPlaintext = showValue ? localValue || revealedValue || "" : "";
    if (isEncrypted && showValue && revealedValue && currentPlaintext === revealedValue && value) {
      return;
    }
    if (!showValue && isEncrypted) {
      return;
    }
    setIsCommitting(true);
    setError(null);
    try {
      await onCommit(currentPlaintext);
      if (showValue && currentPlaintext) {
        setRevealedValue(currentPlaintext);
      }
      if (!currentPlaintext.trim()) {
        setLocalValue("");
        setRevealedValue(null);
      }
    } catch (err) {
      console.error("Failed to commit secret:", err);
      setError(`Failed to save: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setIsCommitting(false);
    }
  }, [showValue, localValue, revealedValue, isEncrypted, value, onCommit, disabled]);
  const busy = isRevealing || isCommitting;
  const derivedHelper = isCommitting ? "Saving..." : isRevealing ? "Revealing..." : isEncrypted ? "Value is encrypted. Click show to reveal." : value ? "Value is not encrypted. Save to encrypt." : "Enter a secret value.";
  return /* @__PURE__ */ jsxs3(Box3, { children: [
    error && /* @__PURE__ */ jsx4(Alert, { severity: "error", sx: { mb: 1 }, onClose: () => setError(null), children: error }),
    /* @__PURE__ */ jsx4(
      TextField2,
      {
        value: displayValue,
        onChange: handleChange,
        onBlur: handleBlur,
        disabled: disabled || busy,
        fullWidth: true,
        type: showValue ? "text" : "password",
        placeholder: placeholder ?? (isEncrypted ? "Click show to reveal" : "Enter secret value..."),
        size: "small",
        InputProps: {
          startAdornment: /* @__PURE__ */ jsx4(InputAdornment, { position: "start", children: isEncrypted ? /* @__PURE__ */ jsx4(Tooltip2, { title: "Encrypted", children: /* @__PURE__ */ jsx4(LockIcon, { fontSize: "small", color: "success" }) }) : value ? /* @__PURE__ */ jsx4(Tooltip2, { title: "Warning: value is not encrypted. Save to encrypt.", children: /* @__PURE__ */ jsx4(WarningIcon, { fontSize: "small", color: "warning" }) }) : null }),
          endAdornment: /* @__PURE__ */ jsx4(InputAdornment, { position: "end", children: busy ? /* @__PURE__ */ jsx4(CircularProgress, { size: 16 }) : /* @__PURE__ */ jsx4(Tooltip2, { title: showValue ? "Hide value" : "Show value", children: /* @__PURE__ */ jsx4(
            IconButton2,
            {
              onClick: handleToggleVisibility,
              disabled: disabled || !isEncrypted && !value,
              edge: "end",
              size: "small",
              children: showValue ? /* @__PURE__ */ jsx4(VisibilityOffIcon, { fontSize: "small" }) : /* @__PURE__ */ jsx4(VisibilityIcon, { fontSize: "small" })
            }
          ) }) })
        },
        helperText: helperText ?? derivedHelper
      }
    )
  ] });
}

// src/components/DateRangeFilter/DateRangeFilter.tsx
import * as React3 from "react";
import { Box as Box6, Button, Divider as Divider2, Popover, Typography as Typography5, alpha as alpha3, useMediaQuery, useTheme as useTheme5 } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { BiCalendar } from "react-icons/bi";

// src/components/DateRangeFilter/PresetRail.tsx
import { Box as Box4, Divider, Typography as Typography3, alpha, useTheme as useTheme3 } from "@mui/material";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var CUSTOM_KEY = "custom";
function PresetRail({ presets, activeKey, referenceDate, onSelect, formatWindow, stacked = false }) {
  const theme = useTheme3();
  const rowSx = (active) => ({
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1,
    width: "100%",
    px: 1.25,
    py: 0.75,
    border: 0,
    borderRadius: 1,
    font: "inherit",
    textAlign: "left",
    cursor: "pointer",
    color: active ? theme.palette.secondary.main : theme.palette.text.primary,
    bgcolor: active ? alpha(theme.palette.secondary.main, 0.1) : "transparent",
    transition: theme.transitions.create(["background-color", "color"], { duration: theme.transitions.duration.shortest }),
    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
    "&:hover": { bgcolor: active ? alpha(theme.palette.secondary.main, 0.14) : theme.palette.action.hover },
    "&:active": { bgcolor: active ? alpha(theme.palette.secondary.main, 0.2) : theme.palette.action.selected },
    "&:focus-visible": { outline: `2px solid ${theme.palette.secondary.main}`, outlineOffset: -2 },
    "&::before": active ? { content: '""', position: "absolute", left: 0, top: 6, bottom: 6, width: 3, borderRadius: 2, bgcolor: theme.palette.secondary.main } : void 0
  });
  const rows = [];
  let lastGroup;
  presets.forEach((p, i) => {
    const group = p.group ?? "";
    if (i > 0 && group !== lastGroup) rows.push(/* @__PURE__ */ jsx5(Divider, { sx: { my: 0.75, mx: 0.75 } }, `div-${i}`));
    lastGroup = group;
    const [s, e] = p.getWindow(referenceDate);
    const active = p.key === activeKey;
    rows.push(
      /* @__PURE__ */ jsxs4(Box4, { component: "button", type: "button", onClick: () => onSelect(p.key), "aria-pressed": active, sx: rowSx(active), children: [
        /* @__PURE__ */ jsx5(Typography3, { component: "span", sx: { fontSize: theme.typography.body2.fontSize, fontWeight: active ? 600 : 500, whiteSpace: "nowrap" }, children: p.label }),
        /* @__PURE__ */ jsx5(
          Typography3,
          {
            component: "span",
            sx: { fontSize: theme.typography.caption.fontSize, color: active ? theme.palette.secondary.main : theme.palette.text.secondary, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
            children: formatWindow(s, e)
          }
        )
      ] }, p.key)
    );
  });
  const customActive = activeKey === CUSTOM_KEY;
  rows.push(/* @__PURE__ */ jsx5(Divider, { sx: { my: 0.75, mx: 0.75 } }, "div-custom"));
  rows.push(
    /* @__PURE__ */ jsx5(Box4, { component: "button", type: "button", onClick: () => onSelect(CUSTOM_KEY), "aria-pressed": customActive, sx: rowSx(customActive), children: /* @__PURE__ */ jsx5(Typography3, { component: "span", sx: { fontSize: theme.typography.body2.fontSize, fontWeight: customActive ? 600 : 500 }, children: "Custom" }) }, CUSTOM_KEY)
  );
  return /* @__PURE__ */ jsx5(
    Box4,
    {
      role: "listbox",
      "aria-label": "Date range presets",
      sx: {
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
        p: 1,
        flex: "none",
        bgcolor: theme.palette.background.paper,
        width: stacked ? "100%" : 190,
        maxHeight: stacked ? 200 : "none",
        overflowY: "auto",
        ...stacked ? { borderBottom: `1px solid ${theme.palette.divider}` } : { borderRight: `1px solid ${theme.palette.divider}` }
      },
      children: rows
    }
  );
}

// src/components/DateRangeFilter/RangeCalendar.tsx
import * as React2 from "react";
import { Box as Box5, IconButton as IconButton3, Typography as Typography4, alpha as alpha2, useTheme as useTheme4 } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// src/components/DateRangeFilter/dateUtils.ts
var startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
var endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
var addDays = (d, n) => {
  const x = startOfDay(d);
  x.setDate(x.getDate() + n);
  return x;
};
var startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
var endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
var addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);
var startOfWeek = (d, weekStartsOn = 0) => {
  const diff = (d.getDay() - weekStartsOn + 7) % 7;
  return addDays(startOfDay(d), -diff);
};
var endOfWeek = (d, weekStartsOn = 0) => addDays(startOfWeek(d, weekStartsOn), 6);
var isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
var isBeforeDay = (a, b) => startOfDay(a).getTime() < startOfDay(b).getTime();
var isAfterDay = (a, b) => startOfDay(a).getTime() > startOfDay(b).getTime();
var isWithinDay = (d, start, end) => {
  const t = startOfDay(d).getTime();
  return t >= startOfDay(start).getTime() && t <= startOfDay(end).getTime();
};
var toIsoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
var parseIsoDay = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : startOfDay(d);
};
var clampDay = (day, min, max) => {
  let d = startOfDay(day);
  if (min && isBeforeDay(d, min)) d = startOfDay(min);
  if (max && isAfterDay(d, max)) d = startOfDay(max);
  return d;
};
var isDayDisabled = (day, min, max) => min != null && isBeforeDay(day, min) || max != null && isAfterDay(day, max);
var resolveWindowFromPreset = (preset, ref) => preset.getWindow(ref);
function resolveEffectiveWindow(value, opts = {}) {
  const ref = opts.referenceDate ?? /* @__PURE__ */ new Date();
  const fallback = () => ({ start: startOfDay(ref), end: endOfDay(ref) });
  if (value.preset === "custom") {
    const s = value.startDate ? parseIsoDay(value.startDate) : null;
    const e = value.endDate ? parseIsoDay(value.endDate) : null;
    if (!s || !e) return fallback();
    let start2 = s;
    let end2 = e;
    if (isAfterDay(start2, end2)) [start2, end2] = [end2, start2];
    start2 = clampDay(start2, opts.minDate, opts.maxDate);
    end2 = clampDay(end2, opts.minDate, opts.maxDate);
    if (isAfterDay(start2, end2)) return fallback();
    return { start: startOfDay(start2), end: endOfDay(end2) };
  }
  const preset = opts.presets?.find((p) => p.key === value.preset);
  if (!preset) return fallback();
  const [start, end] = preset.getWindow(ref);
  return { start: startOfDay(start), end: endOfDay(end) };
}

// src/components/DateRangeFilter/styles.ts
var touchTargetSx = {
  "@media (pointer: coarse)": { minWidth: 36, minHeight: 36 }
};

// src/components/DateRangeFilter/RangeCalendar.tsx
import { jsx as jsx6, jsxs as jsxs5 } from "react/jsx-runtime";
var MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
var WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
var MONTH_WIDTH = 224;
var dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
var shiftMonthKeepDay = (d, n) => {
  const target = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(d.getDate(), lastDay));
  return target;
};
var weeksInGrid = (base, weekStartsOn) => {
  const gridStart = startOfWeek(startOfMonth(base), weekStartsOn);
  const gridEnd = endOfWeek(endOfMonth(base), weekStartsOn);
  return Math.round((gridEnd.getTime() - gridStart.getTime()) / (7 * 864e5)) + 1;
};
function RangeCalendar(props) {
  const { viewMonth, numberOfMonths, weekStartsOn, rangeStart, rangeEnd, isPreview, today, minDate, maxDate, onDayClick, onDayHover, onViewMonthChange } = props;
  const theme = useTheme4();
  const months = React2.useMemo(() => Array.from({ length: numberOfMonths }, (_, i) => addMonths(viewMonth, i)), [viewMonth, numberOfMonths]);
  const weeks = React2.useMemo(() => Math.max(...months.map((m) => weeksInGrid(m, weekStartsOn))), [months, weekStartsOn]);
  const [focusDay, setFocusDay] = React2.useState(() => rangeEnd ?? rangeStart ?? today);
  const dayRefs = React2.useRef(/* @__PURE__ */ new Map());
  const gridHasFocus = React2.useRef(false);
  React2.useEffect(() => {
    const firstDay = startOfMonth(viewMonth);
    const lastDay = endOfMonth(addMonths(viewMonth, numberOfMonths - 1));
    setFocusDay((prev) => prev < firstDay || prev > lastDay ? firstDay : prev);
  }, [viewMonth, numberOfMonths]);
  React2.useEffect(() => {
    if (gridHasFocus.current) dayRefs.current.get(dayKey(focusDay))?.focus();
  }, [focusDay]);
  const moveFocus = (next) => {
    gridHasFocus.current = true;
    const firstDay = startOfMonth(viewMonth);
    const lastDay = endOfMonth(months[months.length - 1]);
    if (next < firstDay) onViewMonthChange(addMonths(viewMonth, -1));
    else if (next > lastDay) onViewMonthChange(addMonths(viewMonth, 1));
    setFocusDay(next);
  };
  const handleKeyDown = (e) => {
    let handled = true;
    switch (e.key) {
      case "ArrowLeft":
        moveFocus(addDays(focusDay, -1));
        break;
      case "ArrowRight":
        moveFocus(addDays(focusDay, 1));
        break;
      case "ArrowUp":
        moveFocus(addDays(focusDay, -7));
        break;
      case "ArrowDown":
        moveFocus(addDays(focusDay, 7));
        break;
      case "Home":
        moveFocus(startOfWeek(focusDay, weekStartsOn));
        break;
      case "End":
        moveFocus(endOfWeek(focusDay, weekStartsOn));
        break;
      case "PageUp":
        moveFocus(shiftMonthKeepDay(focusDay, -1));
        break;
      case "PageDown":
        moveFocus(shiftMonthKeepDay(focusDay, 1));
        break;
      case "Enter":
      case " ":
        if (!isDayDisabled(focusDay, minDate, maxDate)) onDayClick(focusDay);
        break;
      default:
        handled = false;
    }
    if (handled) e.preventDefault();
  };
  const lastHoverIso = React2.useRef(null);
  const handlePointerOver = (e) => {
    const el = e.target.closest?.("[data-day]");
    let iso = el?.dataset.day ?? null;
    if (iso) {
      const d = parseIsoDay(iso);
      if (d && isDayDisabled(d, minDate, maxDate)) iso = null;
    }
    if (iso === lastHoverIso.current) return;
    lastHoverIso.current = iso;
    onDayHover(iso ? parseIsoDay(iso) : null);
  };
  const handlePointerLeave = () => {
    if (lastHoverIso.current === null) return;
    lastHoverIso.current = null;
    onDayHover(null);
  };
  const renderMonthBody = (base) => {
    const gridStart = startOfWeek(startOfMonth(base), weekStartsOn);
    const cells = Array.from({ length: weeks * 7 }, (_, i) => addDays(gridStart, i));
    return /* @__PURE__ */ jsxs5(Box5, { sx: { width: MONTH_WIDTH }, children: [
      /* @__PURE__ */ jsx6(Box5, { sx: { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", mb: 0.25 }, children: Array.from({ length: 7 }, (_, i) => /* @__PURE__ */ jsx6(Box5, { sx: { textAlign: "center", fontSize: 10.5, fontWeight: 600, color: theme.palette.text.secondary }, children: WEEKDAYS[(weekStartsOn + i) % 7] }, i)) }),
      /* @__PURE__ */ jsx6(Box5, { sx: { display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }, onKeyDown: handleKeyDown, children: cells.map((day) => {
        if (day.getMonth() !== base.getMonth()) return /* @__PURE__ */ jsx6(Box5, { sx: { height: 34 }, "aria-hidden": true }, dayKey(day));
        const disabled = isDayDisabled(day, minDate, maxDate);
        const inRange = rangeStart != null && rangeEnd != null && isWithinDay(day, rangeStart, rangeEnd);
        const isStart = rangeStart != null && isSameDay(day, rangeStart);
        const isEnd = rangeEnd != null && isSameDay(day, rangeEnd);
        const isEndpoint = isStart || isEnd;
        const isToday = isSameDay(day, today);
        const isFocusTarget = isSameDay(day, focusDay);
        return /* @__PURE__ */ jsxs5(
          Box5,
          {
            component: "button",
            type: "button",
            ref: (el) => {
              if (el) dayRefs.current.set(dayKey(day), el);
              else dayRefs.current.delete(dayKey(day));
            },
            disabled,
            tabIndex: isFocusTarget ? 0 : -1,
            "aria-label": day.toDateString(),
            "aria-pressed": isEndpoint,
            "data-day": toIsoDay(day),
            onClick: () => !disabled && onDayClick(day),
            onFocus: () => {
              gridHasFocus.current = true;
            },
            onBlur: () => {
              gridHasFocus.current = false;
            },
            sx: {
              position: "relative",
              height: 34,
              border: 0,
              p: 0,
              font: "inherit",
              fontSize: 12.5,
              fontVariantNumeric: "tabular-nums",
              cursor: disabled ? "default" : "pointer",
              color: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
              bgcolor: inRange ? alpha2(theme.palette.secondary.main, isPreview ? 0.08 : 0.12) : "transparent",
              borderTopLeftRadius: isStart ? "999px" : 0,
              borderBottomLeftRadius: isStart ? "999px" : 0,
              borderTopRightRadius: isEnd ? "999px" : 0,
              borderBottomRightRadius: isEnd ? "999px" : 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover > span": !disabled && !isEndpoint ? { bgcolor: theme.palette.action.hover } : void 0,
              "&:active > span": !disabled && !isEndpoint ? { bgcolor: theme.palette.action.selected } : void 0,
              "&:focus-visible": { outline: `2px solid ${theme.palette.secondary.main}`, outlineOffset: -2, borderRadius: "8px" }
            },
            children: [
              /* @__PURE__ */ jsx6(
                Box5,
                {
                  component: "span",
                  sx: {
                    width: 30,
                    height: 30,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    fontWeight: isEndpoint || isToday ? 600 : 400,
                    color: isEndpoint && !isPreview ? theme.palette.secondary.contrastText : "inherit",
                    bgcolor: isEndpoint && !isPreview ? theme.palette.secondary.main : "transparent",
                    boxShadow: isEndpoint && isPreview ? `inset 0 0 0 2px ${theme.palette.secondary.main}` : "none",
                    transition: theme.transitions.create(["background-color"], { duration: theme.transitions.duration.shortest }),
                    "@media (prefers-reduced-motion: reduce)": { transition: "none" }
                  },
                  children: day.getDate()
                }
              ),
              isToday && /* @__PURE__ */ jsx6(
                Box5,
                {
                  component: "span",
                  "aria-hidden": true,
                  sx: {
                    position: "absolute",
                    bottom: 2,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    bgcolor: isEndpoint && !isPreview ? theme.palette.secondary.contrastText : theme.palette.secondary.main
                  }
                }
              )
            ]
          },
          dayKey(day)
        );
      }) })
    ] }, dayKey(base));
  };
  return /* @__PURE__ */ jsxs5(Box5, { sx: { p: 2, pt: 1.5, position: "relative" }, children: [
    /* @__PURE__ */ jsx6(IconButton3, { size: "small", "aria-label": "Previous month", onClick: () => onViewMonthChange(addMonths(viewMonth, -1)), sx: [{ position: "absolute", left: theme.spacing(1), top: theme.spacing(1) }, touchTargetSx], children: /* @__PURE__ */ jsx6(ChevronLeftIcon, { fontSize: "small" }) }),
    /* @__PURE__ */ jsx6(IconButton3, { size: "small", "aria-label": "Next month", onClick: () => onViewMonthChange(addMonths(viewMonth, 1)), sx: [{ position: "absolute", right: theme.spacing(1), top: theme.spacing(1) }, touchTargetSx], children: /* @__PURE__ */ jsx6(ChevronRightIcon, { fontSize: "small" }) }),
    /* @__PURE__ */ jsx6(Box5, { sx: { display: "flex", gap: 2.5, justifyContent: "center", mb: 1.5 }, children: months.map((m) => /* @__PURE__ */ jsxs5(Typography4, { sx: { width: MONTH_WIDTH, textAlign: "center", fontSize: theme.typography.body2.fontSize, fontWeight: 600 }, children: [
      MONTH_NAMES[m.getMonth()],
      " ",
      m.getFullYear()
    ] }, dayKey(m))) }),
    /* @__PURE__ */ jsx6(Box5, { sx: { display: "flex", gap: 2.5 }, onMouseOver: handlePointerOver, onMouseLeave: handlePointerLeave, children: months.map(renderMonthBody) })
  ] });
}

// src/components/DateRangeFilter/presets.ts
function createDefaultDateRangePresets(weekStartsOn = 0) {
  const sow = (d) => startOfWeek(d, weekStartsOn);
  const eow = (d) => endOfWeek(d, weekStartsOn);
  return [
    { key: "tomorrow", label: "Tomorrow", group: "day", getWindow: (ref) => [addDays(ref, 1), addDays(ref, 1)] },
    { key: "today", label: "Today", group: "day", getWindow: (ref) => [startOfDay(ref), startOfDay(ref)] },
    { key: "yesterday", label: "Yesterday", group: "day", getWindow: (ref) => [addDays(ref, -1), addDays(ref, -1)] },
    { key: "nextweek", label: "Next Week", group: "week", getWindow: (ref) => [sow(addDays(ref, 7)), eow(addDays(ref, 7))] },
    { key: "thisweek", label: "This Week", group: "week", getWindow: (ref) => [sow(ref), eow(ref)] },
    { key: "lastweek", label: "Last Week", group: "week", getWindow: (ref) => [sow(addDays(ref, -7)), eow(addDays(ref, -7))] },
    { key: "nextmonth", label: "Next Month", group: "month", getWindow: (ref) => [startOfMonth(addMonths(ref, 1)), endOfMonth(addMonths(ref, 1))] },
    { key: "thismonth", label: "This Month", group: "month", getWindow: (ref) => [startOfMonth(ref), endOfMonth(ref)] },
    { key: "lastmonth", label: "Last Month", group: "month", getWindow: (ref) => [startOfMonth(addMonths(ref, -1)), endOfMonth(addMonths(ref, -1))] }
  ];
}
var defaultDateRangePresets = createDefaultDateRangePresets(0);
function resolveDateRangeWindow(value, opts = {}) {
  const presets = opts.presets ?? createDefaultDateRangePresets(opts.weekStartsOn ?? 0);
  return resolveEffectiveWindow(value, { ...opts, presets });
}

// src/components/DateRangeFilter/DateRangeFilter.tsx
import { Fragment, jsx as jsx7, jsxs as jsxs6 } from "react/jsx-runtime";
var MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
var CUSTOM = "custom";
var sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
function makeDefaultFormatWindow(currentYear) {
  const suffix = (d) => d.getFullYear() !== currentYear ? ` '${String(d.getFullYear()).slice(-2)}` : "";
  return (start, end) => {
    if (sameDay(start, end)) return `${MONTHS[start.getMonth()]} ${start.getDate()}${suffix(start)}`;
    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      return `${MONTHS[start.getMonth()]} ${start.getDate()} - ${end.getDate()}${suffix(end)}`;
    }
    return `${MONTHS[start.getMonth()]} ${start.getDate()}${suffix(start)} - ${MONTHS[end.getMonth()]} ${end.getDate()}${suffix(end)}`;
  };
}
var dayCount = (start, end) => Math.round((startOfDay(end).getTime() - startOfDay(start).getTime()) / 864e5) + 1;
function DateRangeFilter(props) {
  const {
    value,
    onChange,
    referenceDate,
    weekStartsOn = 0,
    minDate,
    maxDate,
    numberOfMonths = 2,
    dense = false,
    size = "small",
    fullWidth = false,
    disabled = false,
    applyOnSelect = false,
    formatWindow: formatWindowProp,
    "aria-label": ariaLabel = "Select date range"
  } = props;
  const theme = useTheme5();
  const inputBorder = alpha3(theme.palette.mode === "dark" ? theme.palette.common.white : theme.palette.common.black, 0.23);
  const isSmall = useMediaQuery("(max-width:680px)");
  const effectiveMonths = isSmall ? 1 : numberOfMonths;
  const ref = React3.useMemo(() => referenceDate ?? /* @__PURE__ */ new Date(), [referenceDate]);
  const formatWindow = React3.useMemo(() => formatWindowProp ?? makeDefaultFormatWindow(ref.getFullYear()), [formatWindowProp, ref]);
  const presets = React3.useMemo(() => props.presets ?? createDefaultDateRangePresets(weekStartsOn), [props.presets, weekStartsOn]);
  const resolveOpts = React3.useMemo(() => ({ referenceDate: ref, presets, minDate, maxDate }), [ref, presets, minDate, maxDate]);
  const [open, setOpen] = React3.useState(false);
  const triggerRef = React3.useRef(null);
  const [draft, setDraft] = React3.useState(value);
  const [customAnchor, setCustomAnchor] = React3.useState(null);
  const [hoverDay, setHoverDay] = React3.useState(null);
  const [viewMonth, setViewMonth] = React3.useState(() => startOfMonth(resolveEffectiveWindow(value, { referenceDate: ref, presets, minDate, maxDate }).start));
  const committedWindow = resolveEffectiveWindow(value, resolveOpts);
  const draftWindow = resolveEffectiveWindow(draft, resolveOpts);
  const isCustom = value.preset === CUSTOM;
  const activePreset = presets.find((p) => p.key === value.preset);
  const triggerLabel = isCustom ? "Custom" : activePreset?.label ?? value.preset;
  const triggerWindowText = formatWindow(committedWindow.start, committedWindow.end);
  const commit = (next) => onChange(next, resolveEffectiveWindow(next, resolveOpts));
  const openPopover = () => {
    setDraft(value);
    setCustomAnchor(null);
    setHoverDay(null);
    setViewMonth(startOfMonth(committedWindow.start));
    setOpen(true);
  };
  const closePopover = () => {
    setOpen(false);
    setCustomAnchor(null);
    setHoverDay(null);
  };
  const handlePresetSelect = (key) => {
    if (key === CUSTOM) {
      const w = resolveEffectiveWindow(draft, resolveOpts);
      const next2 = { preset: CUSTOM, startDate: toIsoDay(w.start), endDate: toIsoDay(w.end) };
      setDraft(next2);
      setCustomAnchor(null);
      setViewMonth(startOfMonth(w.start));
      if (applyOnSelect) commit(next2);
      return;
    }
    const next = { preset: key, startDate: null, endDate: null };
    setDraft(next);
    setCustomAnchor(null);
    setViewMonth(startOfMonth(resolveEffectiveWindow(next, resolveOpts).start));
    if (applyOnSelect) {
      commit(next);
      closePopover();
    }
  };
  const handleDayClick = (day) => {
    if (customAnchor === null) {
      setCustomAnchor(day);
      setDraft({ preset: CUSTOM, startDate: toIsoDay(day), endDate: toIsoDay(day) });
      return;
    }
    const [start, end] = isAfterDay(customAnchor, day) ? [day, customAnchor] : [customAnchor, day];
    const next = { preset: CUSTOM, startDate: toIsoDay(start), endDate: toIsoDay(end) };
    setCustomAnchor(null);
    setDraft(next);
    if (applyOnSelect) {
      commit(next);
      closePopover();
    }
  };
  let rangeStart = draftWindow.start;
  let rangeEnd = draftWindow.end;
  let isPreview = false;
  if (draft.preset === CUSTOM && customAnchor && hoverDay) {
    [rangeStart, rangeEnd] = isAfterDay(customAnchor, hoverDay) ? [hoverDay, customAnchor] : [customAnchor, hoverDay];
    isPreview = true;
  }
  const height = dense ? 32 : size === "medium" ? 40 : 36;
  return /* @__PURE__ */ jsxs6(Fragment, { children: [
    /* @__PURE__ */ jsxs6(
      Box6,
      {
        component: "button",
        type: "button",
        ref: triggerRef,
        disabled,
        "aria-label": ariaLabel,
        "aria-haspopup": "dialog",
        "aria-expanded": open,
        onClick: () => open ? closePopover() : openPopover(),
        sx: {
          display: "inline-flex",
          alignItems: "center",
          gap: dense ? 0.75 : 1,
          height,
          px: dense ? 1.25 : 1.5,
          width: fullWidth ? "100%" : "auto",
          minWidth: dense ? 0 : 200,
          font: "inherit",
          fontSize: theme.typography.body2.fontSize,
          color: theme.palette.text.primary,
          bgcolor: "transparent",
          border: `1px solid ${open ? theme.palette.secondary.main : inputBorder}`,
          borderRadius: dense ? 999 : `${theme.shape.borderRadius}px`,
          boxShadow: open ? `0 0 0 2px ${alpha3(theme.palette.secondary.main, 0.2)}` : "none",
          cursor: disabled ? "default" : "pointer",
          userSelect: "none",
          transition: theme.transitions.create(["border-color", "box-shadow", "background-color"], { duration: theme.transitions.duration.shortest }),
          opacity: disabled ? theme.palette.action.disabledOpacity : 1,
          "&:hover": disabled ? void 0 : { bgcolor: theme.palette.action.hover },
          "&:active": disabled ? void 0 : { bgcolor: theme.palette.action.selected },
          "&:focus-visible": { outline: `2px solid ${theme.palette.secondary.main}`, outlineOffset: 2, borderColor: theme.palette.secondary.main },
          "@media (prefers-reduced-motion: reduce)": { transition: "none" }
        },
        children: [
          /* @__PURE__ */ jsx7(Box6, { component: "span", "aria-hidden": true, sx: { display: "flex", flex: "none", fontSize: dense ? 15 : 17, color: theme.palette.text.secondary }, children: /* @__PURE__ */ jsx7(BiCalendar, {}) }),
          /* @__PURE__ */ jsx7(Typography5, { component: "span", sx: { fontSize: "inherit", fontWeight: 600, whiteSpace: "nowrap" }, children: triggerLabel }),
          !dense && triggerWindowText && /* @__PURE__ */ jsx7(Typography5, { component: "span", sx: { fontSize: "inherit", color: theme.palette.text.secondary, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", ml: 0.5 }, children: triggerWindowText }),
          /* @__PURE__ */ jsx7(
            KeyboardArrowDownIcon,
            {
              sx: {
                fontSize: dense ? 16 : 18,
                color: theme.palette.text.secondary,
                ml: "auto",
                flex: "none",
                transform: open ? "rotate(180deg)" : "none",
                transition: theme.transitions.create("transform", { duration: theme.transitions.duration.shorter }),
                "@media (prefers-reduced-motion: reduce)": { transition: "none" }
              }
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsxs6(
      Popover,
      {
        open,
        anchorEl: triggerRef.current,
        onClose: closePopover,
        anchorOrigin: { vertical: "bottom", horizontal: "left" },
        transformOrigin: { vertical: "top", horizontal: "left" },
        slotProps: { paper: { sx: { mt: 1, borderRadius: "12px", boxShadow: theme.shadows[8], overflow: "hidden", maxWidth: "calc(100vw - 32px)" } } },
        children: [
          /* @__PURE__ */ jsxs6(Box6, { role: "dialog", "aria-label": "Select date range", sx: { display: "flex", flexDirection: isSmall ? "column" : "row" }, children: [
            /* @__PURE__ */ jsx7(PresetRail, { presets, activeKey: draft.preset, referenceDate: ref, onSelect: handlePresetSelect, formatWindow, stacked: isSmall }),
            /* @__PURE__ */ jsx7(
              RangeCalendar,
              {
                viewMonth,
                numberOfMonths: effectiveMonths,
                weekStartsOn,
                rangeStart,
                rangeEnd,
                isPreview,
                today: ref,
                minDate,
                maxDate,
                onDayClick: handleDayClick,
                onDayHover: setHoverDay,
                onViewMonthChange: setViewMonth
              }
            )
          ] }),
          /* @__PURE__ */ jsx7(Divider2, {}),
          /* @__PURE__ */ jsxs6(Box6, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, px: 2, py: 1.25 }, children: [
            /* @__PURE__ */ jsxs6(Typography5, { sx: { fontSize: theme.typography.body2.fontSize, color: theme.palette.text.secondary }, children: [
              /* @__PURE__ */ jsx7(Box6, { component: "span", sx: { color: theme.palette.text.primary, fontWeight: 600, fontVariantNumeric: "tabular-nums" }, children: formatWindow(draftWindow.start, draftWindow.end) }),
              /* @__PURE__ */ jsxs6(Box6, { component: "span", sx: { color: theme.palette.text.disabled, ml: 2 }, children: [
                dayCount(draftWindow.start, draftWindow.end),
                " day",
                dayCount(draftWindow.start, draftWindow.end) > 1 ? "s" : ""
              ] })
            ] }),
            /* @__PURE__ */ jsx7(Box6, { sx: { display: "flex", gap: 1 }, children: applyOnSelect ? /* @__PURE__ */ jsx7(Button, { size: "small", variant: "text", color: "inherit", onClick: closePopover, sx: touchTargetSx, children: "Done" }) : /* @__PURE__ */ jsxs6(Fragment, { children: [
              /* @__PURE__ */ jsx7(Button, { size: "small", variant: "text", color: "inherit", onClick: closePopover, sx: touchTargetSx, children: "Cancel" }),
              /* @__PURE__ */ jsx7(
                Button,
                {
                  size: "small",
                  variant: "contained",
                  color: "primary",
                  onClick: () => {
                    commit(draft);
                    closePopover();
                  },
                  sx: touchTargetSx,
                  children: "Apply"
                }
              )
            ] }) })
          ] })
        ]
      }
    )
  ] });
}

// src/components/HeroPanel/HeroPanel.tsx
import { Box as Box7 } from "@mui/material";
import { alpha as alpha4 } from "@mui/material/styles";
import { jsx as jsx8 } from "react/jsx-runtime";
var HERO_PANEL_NARROW = "@container (max-width: 639px)";
var glowBackground = (color) => `radial-gradient(circle at 88% 10%, ${alpha4(color, 0.34)} 0%, ${alpha4(color, 0)} 48%)`;
var HeroPanel = ({ children, glow, component = "section", sx, ...rest }) => /* @__PURE__ */ jsx8(
  Box7,
  {
    component,
    "aria-label": rest["aria-label"],
    sx: [
      (theme) => ({
        containerType: "inline-size",
        position: "relative",
        overflow: "hidden",
        isolation: "isolate",
        bgcolor: "hero.background",
        color: "hero.contrastText",
        borderRadius: 2,
        p: 4,
        display: "flex",
        flexDirection: "column",
        gap: 3,
        [HERO_PANEL_NARROW]: { p: 2.5, gap: 2.5 },
        // The glow: behind everything, clipped by the panel, never catching the pointer.
        ...glow != null && {
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            zIndex: -1,
            pointerEvents: "none",
            backgroundImage: glowBackground(typeof glow === "function" ? glow(theme) : glow)
          }
        }
      }),
      ...Array.isArray(sx) ? sx : sx != null ? [sx] : []
    ],
    children
  }
);
var HeroPanel_default = HeroPanel;

// src/components/HeroPanel/heroButton.ts
import { alpha as alpha5 } from "@mui/material/styles";
var BASE = { textTransform: "none", fontWeight: 600, borderRadius: 1 };
var heroButtonSx = (emphasis) => emphasis === "primary" ? BASE : {
  ...BASE,
  color: "common.white",
  borderColor: (theme) => alpha5(theme.palette.common.white, 0.4),
  "&:hover": {
    borderColor: "common.white",
    bgcolor: (theme) => alpha5(theme.palette.common.white, 0.08)
  }
};

// src/components/HeroPanel/heroText.ts
var heroTitleSx = { fontSize: 20, fontWeight: 700, lineHeight: 1.15, letterSpacing: "-0.01em" };
var heroLedeSx = { mt: 0.75, fontSize: 14, fontWeight: 500, lineHeight: 1.5, color: "hero.contrastSecondary", maxWidth: "60ch" };

// src/components/Divider/Divider.tsx
import { css } from "@emotion/css";
import { useTheme as useTheme6, Tooltip as Tooltip3, Typography as Typography6 } from "@mui/material";
import { BiSolidInfoCircle } from "react-icons/bi";
import { Fragment as Fragment2, jsx as jsx9, jsxs as jsxs7 } from "react/jsx-runtime";
var Divider3 = ({ children, spacer, mt = 0, mb = 0, actions, tip }) => {
  const theme = useTheme6();
  const cls = useStyles(theme);
  return /* @__PURE__ */ jsxs7(Fragment2, { children: [
    spacer && /* @__PURE__ */ jsx9("div", { className: cls.dividerSpacer }),
    /* @__PURE__ */ jsxs7("div", { className: cls.dividerFlex, style: { marginTop: `${mt}px`, marginBottom: `${mb}px` }, children: [
      /* @__PURE__ */ jsx9("div", { className: cls.dividerItemStretch, children: /* @__PURE__ */ jsx9("div", { className: cls.dividerLine }) }),
      /* @__PURE__ */ jsx9("div", { className: cls.dividerItem, children: /* @__PURE__ */ jsx9(Typography6, { sx: { textTransform: `uppercase`, fontWeight: 600, color: theme?.palette?.text?.disabled }, children }) }),
      /* @__PURE__ */ jsxs7("div", { className: cls.dividerItemStretch, children: [
        /* @__PURE__ */ jsx9("div", { className: cls.dividerLine }),
        /* @__PURE__ */ jsxs7("div", { className: cls.dividerItemExtras, children: [
          actions,
          tip ? /* @__PURE__ */ jsx9(Tooltip3, { placement: "top-end", title: tip, children: /* @__PURE__ */ jsx9("div", { children: /* @__PURE__ */ jsx9(
            BiSolidInfoCircle,
            {
              style: { display: `block`, cursor: `pointer` },
              size: 20,
              color: theme?.palette?.text?.disabled
            }
          ) }) }) : null
        ] })
      ] })
    ] })
  ] });
};
var useStyles = (theme) => {
  return {
    dividerFlex: css`
      display: flex;
      flex-wrap: nowrap;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    `,
    dividerItem: css`
      white-space: nowrap;
    `,
    dividerItemStretch: css`
      display: flex;
      flex-wrap: nowrap;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      flex: 1;
    `,
    dividerItemExtras: css`
      display: flex;
      flex-wrap: nowrap;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    `,
    dividerLine: css`
      width: 100%;
      border-top: 1px solid ${theme?.palette?.divider};
    `,
    dividerSpacer: css`
      width: 100%;
      height: 48px;
      ${theme?.breakpoints?.down("sm")} {
        height: 36px;
      }
      ${theme?.breakpoints?.down("xs")} {
        height: 24px;
      }
    `
  };
};
var Divider_default = Divider3;

// src/components/DataTable/DataTable.tsx
import React4 from "react";
import {
  Box as Box8,
  Button as Button2,
  IconButton as IconButton4,
  Paper,
  TableCell,
  TableRow,
  TablePagination,
  Typography as Typography7,
  Skeleton
} from "@mui/material";
import { styled as styled2 } from "@mui/material/styles";
import { BiChevronDown } from "react-icons/bi";

// src/components/DataTable/dataTableResponsive.ts
var DATA_TABLE_MOBILE_BREAKPOINT = 640;
var dataTableDesktopOnlySx = (breakpoint = DATA_TABLE_MOBILE_BREAKPOINT) => ({
  [`@media (max-width:${breakpoint - 0.05}px)`]: { display: "none" }
});

// src/components/DataTable/DataTable.tsx
import { Fragment as Fragment3, jsx as jsx10, jsxs as jsxs8 } from "react/jsx-runtime";
var DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
var TABLE_DEFAULTS = { size: "small", stickyHeader: true };
var SHADOW_LEVEL1 = "0px 1px 3px rgba(0,0,0,0.06), 0px 1px 2px rgba(0,0,0,0.04)";
var DataTableContainer = styled2(Paper)({
  borderRadius: 12,
  boxShadow: SHADOW_LEVEL1,
  overflow: "hidden"
});
var surfaceTint = (theme) => theme.palette.mode === "dark" ? theme.palette.action.hover : theme.palette.grey[50];
var HeaderCell = styled2(TableCell)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  fontFamily: '"Inter", sans-serif',
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  whiteSpace: "nowrap",
  color: theme.palette.mode === "dark" ? theme.palette.grey[300] : theme.palette.grey[600],
  backgroundColor: theme.palette.background.paper,
  backgroundImage: `linear-gradient(${surfaceTint(theme)}, ${surfaceTint(theme)})`,
  padding: "10px 12px"
}));
var NumericHeaderCell = styled2(HeaderCell)({
  textAlign: "right"
});
var BodyCell = styled2(TableCell)({
  fontFamily: '"Inter Tight", "Inter", sans-serif',
  fontSize: 14,
  fontWeight: 400,
  padding: "8px 12px"
});
var NumericBodyCell = styled2(BodyCell)({
  textAlign: "right",
  fontWeight: 600,
  fontVariantNumeric: "tabular-nums"
});
var IdCell = styled2(TableCell)(({ theme }) => ({
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: 13,
  color: theme.palette.secondary.main,
  padding: "8px 12px"
}));
var ExpandToggleCell = ({
  isOpen,
  onToggle,
  ariaLabel = "Toggle details"
}) => /* @__PURE__ */ jsx10(TableCell, { sx: { width: 28, padding: "6px 0 6px 10px", verticalAlign: "middle" }, children: /* @__PURE__ */ jsx10(
  IconButton4,
  {
    size: "small",
    "aria-label": ariaLabel,
    "aria-expanded": isOpen,
    onClick: (event) => {
      event.stopPropagation();
      onToggle();
    },
    sx: {
      padding: "2px",
      color: "grey.600",
      transition: "transform 150ms ease",
      transform: isOpen ? "rotate(180deg)" : "none"
    },
    children: /* @__PURE__ */ jsx10(BiChevronDown, { size: 12 })
  }
) });
var PinnedActionsCell = ({ variant = "body", sx, children }) => {
  const Base = variant === "head" ? HeaderCell : BodyCell;
  return /* @__PURE__ */ jsx10(
    Base,
    {
      component: variant === "head" ? "th" : "td",
      sx: [
        (theme) => ({
          position: "sticky",
          right: 0,
          zIndex: variant === "head" ? 3 : 2,
          textAlign: "right",
          backgroundColor: theme.palette.background.paper,
          backgroundImage: `linear-gradient(${surfaceTint(theme)}, ${surfaceTint(theme)})`,
          boxShadow: variant === "head" ? "none" : `inset 1px 0 0 ${theme.palette.divider}`
        }),
        ...Array.isArray(sx) ? sx : [sx]
      ],
      children
    }
  );
};
var DataTableMobileCards = ({
  breakpoint = DATA_TABLE_MOBILE_BREAKPOINT,
  sx,
  children
}) => /* @__PURE__ */ jsx10(
  Box8,
  {
    sx: [
      {
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        [`@media (min-width:${breakpoint}px)`]: { display: "none" }
      },
      ...Array.isArray(sx) ? sx : [sx]
    ],
    children
  }
);
var StyledClickableRow = styled2(TableRow)(({ theme, selected }) => ({
  cursor: "pointer",
  transition: "background-color 150ms ease",
  backgroundColor: selected ? theme.palette.action.selected : void 0,
  "&:hover": {
    backgroundColor: theme.palette.action.hover
  }
}));
var ClickableRow = ({ onClick, selected, ariaLabel, children, ...rest }) => /* @__PURE__ */ jsx10(
  StyledClickableRow,
  {
    selected,
    onClick,
    tabIndex: 0,
    role: "button",
    "aria-label": ariaLabel,
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick(e);
      }
    },
    ...rest,
    children
  }
);
var DataTableEmpty = ({
  colSpan,
  variant = "no-matches",
  message = "No results found",
  onClearFilters,
  icon,
  ctaLabel,
  onCtaClick
}) => /* @__PURE__ */ jsx10(TableRow, { children: /* @__PURE__ */ jsx10(TableCell, { colSpan, sx: { py: "40px", textAlign: "center", border: "none" }, children: variant === "zero-data" ? /* @__PURE__ */ jsxs8(Box8, { sx: { display: "flex", flexDirection: "column", alignItems: "center", gap: "16px" }, children: [
  icon && /* @__PURE__ */ jsx10(
    Box8,
    {
      sx: {
        width: 96,
        height: 96,
        borderRadius: "50%",
        bgcolor: "secondary.lighter",
        color: "secondary.main",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: "8px"
      },
      children: React4.cloneElement(icon, {
        sx: { fontSize: 48, color: "inherit", ...icon.props?.sx || {} }
      })
    }
  ),
  /* @__PURE__ */ jsx10(
    Typography7,
    {
      sx: {
        fontFamily: '"Inter", sans-serif',
        fontSize: 20,
        fontWeight: 600,
        lineHeight: 1.3,
        letterSpacing: "-0.01em",
        color: "text.primary"
      },
      children: message
    }
  ),
  /* @__PURE__ */ jsx10(Typography7, { variant: "body2", sx: { color: "grey.600", maxWidth: 260 }, children: "Try adjusting your filters or create a new item." }),
  /* @__PURE__ */ jsxs8(Box8, { sx: { display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", mt: "12px" }, children: [
    ctaLabel && onCtaClick && /* @__PURE__ */ jsx10(
      Button2,
      {
        variant: "contained",
        disableElevation: true,
        onClick: onCtaClick,
        sx: {
          textTransform: "none",
          fontWeight: 600,
          fontSize: 14,
          borderRadius: "8px",
          px: "24px",
          py: "12px"
        },
        children: ctaLabel
      }
    ),
    onClearFilters && /* @__PURE__ */ jsx10(
      Button2,
      {
        variant: "text",
        onClick: onClearFilters,
        sx: {
          textTransform: "none",
          fontWeight: 500,
          fontSize: 13,
          color: "grey.600",
          borderRadius: "8px",
          "&:hover": { bgcolor: "action.hover", color: "text.primary" }
        },
        children: "Clear filters"
      }
    )
  ] })
] }) : /* @__PURE__ */ jsxs8(Fragment3, { children: [
  /* @__PURE__ */ jsx10(Typography7, { variant: "body1", color: "text.secondary", children: message }),
  onClearFilters && /* @__PURE__ */ jsx10(
    Button2,
    {
      variant: "text",
      onClick: onClearFilters,
      sx: {
        mt: 1,
        textTransform: "none",
        fontWeight: 500,
        fontSize: 13,
        color: "grey.600",
        borderRadius: "8px",
        "&:hover": { bgcolor: "action.hover", color: "text.primary" }
      },
      children: "Clear filters"
    }
  )
] }) }) });
var DataTableLoading = ({ colSpan, rows = 5 }) => /* @__PURE__ */ jsx10(Fragment3, { children: Array.from({ length: rows }).map((_, i) => /* @__PURE__ */ jsx10(TableRow, { sx: { height: 50 }, children: Array.from({ length: colSpan }).map((_2, j) => /* @__PURE__ */ jsx10(TableCell, { sx: { padding: "8px 12px" }, children: /* @__PURE__ */ jsx10(Skeleton, { animation: "wave" }) }, j)) }, i)) });
var DataTablePagination = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS
}) => /* @__PURE__ */ jsx10(
  TablePagination,
  {
    component: "div",
    count,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    rowsPerPageOptions,
    sx: {
      borderTop: "1px solid",
      borderColor: "divider",
      "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
        fontSize: 12
      }
    }
  }
);

// src/components/KpiCard/KpiCard.tsx
import { memo } from "react";
import { Box as Box9, Card, Typography as Typography8, Skeleton as Skeleton2 } from "@mui/material";
import { BiTrendingUp, BiTrendingDown, BiMinus } from "react-icons/bi";
import { Fragment as Fragment4, jsx as jsx11, jsxs as jsxs9 } from "react/jsx-runtime";
var SIZE_CONFIG = {
  large: {
    padding: 3,
    gap: "4px",
    borderRadius: "12px",
    titleWidth: 80,
    valueFontSize: 40,
    valueLetterSpacing: "-0.03em",
    valueSkeletonWidth: 100,
    unitFontSize: 12,
    footerSkeletonWidth: 120,
    footerFontSize: 12,
    trendIconSize: 18,
    hoverTranslate: "translateY(-2px)"
  },
  small: {
    padding: "12px 12px 8px 12px",
    gap: "2px",
    borderRadius: "8px",
    titleWidth: 60,
    valueFontSize: 24,
    valueLetterSpacing: "-0.02em",
    valueSkeletonWidth: 60,
    unitFontSize: 10,
    footerSkeletonWidth: 80,
    footerFontSize: 11,
    trendIconSize: 14,
    hoverTranslate: "translateY(-1px)"
  }
};
var DEFAULT_TREND_ICONS = { up: BiTrendingUp, down: BiTrendingDown, flat: BiMinus };
function formatValue(value, format = "number") {
  if (typeof value === "string") return value;
  if (isNaN(value)) return "0";
  switch (format) {
    case "compact":
      if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
      if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
      return value.toLocaleString();
    case "currency":
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0
      }).format(value);
    case "percentage":
      return `${value}%`;
    case "decimal":
      return value.toFixed(1);
    default:
      return value.toLocaleString();
  }
}
var SHADOW_LEVEL2 = "0px 4px 12px rgba(0,0,0,0.08), 0px 2px 4px rgba(0,0,0,0.04)";
var SHADOW_LEVEL3 = "0px 8px 24px rgba(0,0,0,0.10), 0px 4px 8px rgba(0,0,0,0.05)";
var ACCENTS = {
  primary: { main: "primary.main", fill: (theme) => `${theme.palette.primary.main}14` },
  secondaryAccent: { main: "secondaryAccent.main", fill: "secondaryAccent.50" }
};
var KpiCard = memo(function KpiCard2({
  title,
  value,
  unit,
  loading = false,
  format = "number",
  trend,
  trendLabel,
  subtitle,
  percentage,
  children,
  onClick,
  selected = false,
  highlight = false,
  "aria-label": ariaLabel,
  size = "large",
  accent = "primary",
  buttonWhenClickable = false,
  valueSx,
  trendIcons = DEFAULT_TREND_ICONS
}) {
  const s = SIZE_CONFIG[size];
  const tone = ACCENTS[accent];
  const asButton = buttonWhenClickable && !!onClick;
  const TrendIcon = trend === void 0 ? null : Math.abs(trend) < 0.1 ? trendIcons.flat : trend > 0 ? trendIcons.up : trendIcons.down;
  return /* @__PURE__ */ jsxs9(
    Card,
    {
      role: asButton ? "button" : "region",
      "aria-label": ariaLabel || `${title}: ${typeof value === "number" ? formatValue(value, format) : value}`,
      "aria-pressed": asButton ? selected : void 0,
      tabIndex: buttonWhenClickable && !onClick ? void 0 : 0,
      onClick,
      onKeyDown: asButton ? (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      } : void 0,
      sx: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: s.gap,
        p: s.padding,
        borderRadius: s.borderRadius,
        bgcolor: selected ? tone.fill : "background.paper",
        boxShadow: selected ? SHADOW_LEVEL3 : SHADOW_LEVEL2,
        border: "2px solid",
        borderColor: selected ? tone.main : highlight ? (theme) => `${theme.palette.warning.main}80` : "transparent",
        cursor: onClick ? "pointer" : "default",
        transition: "transform 200ms ease-in-out, box-shadow 200ms ease-in-out",
        "&:hover": {
          transform: onClick ? s.hoverTranslate : void 0,
          boxShadow: onClick ? SHADOW_LEVEL3 : void 0,
          borderColor: (theme) => !selected && highlight ? theme.palette.warning.main : void 0
        },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: tone.main,
          outlineOffset: "2px"
        }
      },
      children: [
        /* @__PURE__ */ jsx11(
          Typography8,
          {
            variant: "overline",
            sx: { color: "grey.500", display: "block", ...size === "small" && { fontSize: 10, lineHeight: 1.2 } },
            children: loading ? /* @__PURE__ */ jsx11(Skeleton2, { width: s.titleWidth }) : title
          }
        ),
        /* @__PURE__ */ jsx11(
          Typography8,
          {
            sx: [
              {
                fontSize: s.valueFontSize,
                fontWeight: 800,
                lineHeight: 1.1,
                letterSpacing: s.valueLetterSpacing,
                color: "text.primary"
              },
              ...Array.isArray(valueSx) ? valueSx : [valueSx]
            ],
            children: loading ? /* @__PURE__ */ jsx11(Skeleton2, { width: s.valueSkeletonWidth }) : /* @__PURE__ */ jsxs9(Fragment4, { children: [
              formatValue(value, format),
              unit && /* @__PURE__ */ jsx11(
                Typography8,
                {
                  component: "span",
                  sx: { fontSize: s.unitFontSize, fontWeight: 400, color: "grey.500", ml: 0.5 },
                  children: unit
                }
              )
            ] })
          }
        ),
        loading ? /* @__PURE__ */ jsx11(Skeleton2, { width: s.footerSkeletonWidth, sx: { fontSize: s.footerFontSize } }) : children ? children : /* @__PURE__ */ jsxs9(Fragment4, { children: [
          trend !== void 0 && TrendIcon && /* @__PURE__ */ jsxs9(Box9, { sx: { display: "flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap" }, children: [
            /* @__PURE__ */ jsx11(
              Box9,
              {
                sx: {
                  display: "flex",
                  alignItems: "center",
                  color: trend > 0 ? "success.main" : trend < 0 ? "error.main" : "grey.500"
                },
                children: /* @__PURE__ */ jsx11(TrendIcon, { size: s.trendIconSize })
              }
            ),
            /* @__PURE__ */ jsxs9(
              Typography8,
              {
                sx: {
                  fontSize: s.footerFontSize,
                  fontWeight: 600,
                  color: trend > 0 ? "success.main" : trend < 0 ? "error.main" : "grey.500"
                },
                children: [
                  Math.abs(trend).toFixed(1),
                  "%"
                ]
              }
            ),
            trendLabel && /* @__PURE__ */ jsx11(Typography8, { sx: { fontSize: s.footerFontSize, color: "grey.500", ml: "2px" }, children: trendLabel })
          ] }),
          percentage !== void 0 && /* @__PURE__ */ jsxs9(
            Typography8,
            {
              sx: {
                fontSize: s.footerFontSize,
                color: percentage > 0 ? "success.main" : "error.main"
              },
              children: [
                percentage.toFixed(1),
                "% of total"
              ]
            }
          ),
          subtitle && /* @__PURE__ */ jsx11(Typography8, { sx: { fontSize: s.footerFontSize, color: "grey.500" }, children: subtitle })
        ] })
      ]
    }
  );
});

// src/components/PhoneNumberField.tsx
import { useEffect as useEffect3, useState as useState5 } from "react";
import { TextField as TextField3, InputAdornment as InputAdornment2 } from "@mui/material";
import { Phone as PhoneIcon } from "@mui/icons-material";

// src/phone.ts
var INVALID = { isValid: false, digits: null, formatted: null, e164: null };
var HAS_LETTER = /\p{L}/u;
var NANP_NATIONAL = /^[2-9]\d{2}[2-9]\d{6}$/;
function phoneDigits(input) {
  return (input ?? "").replace(/[^0-9]/g, "");
}
function parsePhoneNumber(input) {
  const raw = input ?? "";
  if (HAS_LETTER.test(raw)) return INVALID;
  let digits = phoneDigits(raw);
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  if (!NANP_NATIONAL.test(digits)) return INVALID;
  return {
    isValid: true,
    digits,
    formatted: `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`,
    e164: `+1${digits}`
  };
}
function formatPhoneNumberAsTyped(digits) {
  if (!digits) return "";
  if (digits.startsWith("1")) {
    const rest = formatNational(digits.slice(1));
    return rest ? `+1 ${rest}` : "+1";
  }
  return formatNational(digits);
}
function formatNational(digits) {
  const area = digits.slice(0, 3);
  const exchange = digits.slice(3, 6);
  const line = digits.slice(6, 10);
  const extra = digits.slice(10);
  if (!area) return "";
  if (!exchange) return `(${area}`;
  if (!line) return `(${area}) ${exchange}`;
  return `(${area}) ${exchange}-${line}${extra ? ` ${extra}` : ""}`;
}

// src/components/PhoneNumberField.tsx
import { jsx as jsx12 } from "react/jsx-runtime";
var DEFAULT_INVALID_TEXT = "Enter a 10-digit US phone number";
var digitsFromValue = (value) => parsePhoneNumber(value).digits ?? phoneDigits(value);
function PhoneNumberField({
  value,
  onChange,
  persistAs = "formatted",
  invalidText = DEFAULT_INVALID_TEXT,
  helperText,
  onBlur,
  InputProps,
  inputProps,
  placeholder = "(555) 555-5555",
  ...rest
}) {
  const [digits, setDigits] = useState5(() => digitsFromValue(value));
  const [showError, setShowError] = useState5(false);
  useEffect3(() => {
    setDigits(digitsFromValue(value));
    setShowError(false);
  }, [value]);
  const display = formatPhoneNumberAsTyped(digits);
  const handleChange = (e) => {
    let next = phoneDigits(e.target.value);
    if (next === digits && e.target.value.length < display.length) next = digits.slice(0, -1);
    setDigits(next);
    if (showError) setShowError(next !== "" && !parsePhoneNumber(next).isValid);
  };
  const handleBlur = (e) => {
    onBlur?.(e);
    if (digits === "") {
      setShowError(false);
      if ((value ?? "") !== "") onChange("");
      return;
    }
    const parsed = parsePhoneNumber(digits);
    if (!parsed.isValid) {
      setShowError(true);
      return;
    }
    setShowError(false);
    const next = persistAs === "digits" ? parsed.digits : persistAs === "e164" ? parsed.e164 : parsed.formatted;
    if (next !== value) onChange(next);
  };
  return /* @__PURE__ */ jsx12(
    TextField3,
    {
      ...rest,
      type: "tel",
      value: display,
      placeholder,
      onChange: handleChange,
      onBlur: handleBlur,
      error: showError,
      helperText: showError ? invalidText : helperText,
      inputProps: { inputMode: "tel", autoComplete: "tel", ...inputProps },
      InputProps: {
        startAdornment: /* @__PURE__ */ jsx12(InputAdornment2, { position: "start", children: /* @__PURE__ */ jsx12(PhoneIcon, { fontSize: "small", color: "disabled" }) }),
        ...InputProps
      }
    }
  );
}

// src/hooks/useOverflowFit.ts
import { useCallback as useCallback3, useEffect as useEffect4, useLayoutEffect, useRef as useRef3, useState as useState6 } from "react";
function useOverflowFit(steps) {
  const [dropped, setDropped] = useState6(0);
  const node = useRef3(null);
  const observer = useRef3(null);
  const lastWidth = useRef3(null);
  const ref = useCallback3((next) => {
    node.current = next;
    observer.current?.disconnect();
    if (!next || typeof ResizeObserver === "undefined") return;
    observer.current = new ResizeObserver(() => {
      const width = node.current?.clientWidth ?? 0;
      if (width === 0 || width === lastWidth.current) return;
      lastWidth.current = width;
      setDropped(0);
    });
    observer.current.observe(next);
  }, []);
  useLayoutEffect(() => {
    const el = node.current;
    if (!el || dropped >= steps) return;
    if (el.scrollWidth > el.clientWidth + 1) setDropped((current) => Math.min(current + 1, steps));
  });
  useEffect4(() => () => observer.current?.disconnect(), []);
  return [ref, dropped];
}

// src/hooks/useFitText.ts
import { useCallback as useCallback4, useEffect as useEffect5, useLayoutEffect as useLayoutEffect2, useRef as useRef4, useState as useState7 } from "react";
var MIN_STEP_PX = 1;
var OVERFLOW_TOLERANCE_PX = 1;
function useFitText(text, { maxFontSizePx, minFontSizePx }) {
  const [fontSize, setFontSize] = useState7(maxFontSizePx);
  const [, setMeasurement] = useState7(0);
  const node = useRef4(null);
  const observer = useRef4(null);
  const lastWidth = useRef4(null);
  const remeasure = useCallback4(() => {
    setFontSize(maxFontSizePx);
    setMeasurement((count) => count + 1);
  }, [maxFontSizePx]);
  const ref = useCallback4((next) => {
    node.current = next;
    observer.current?.disconnect();
    if (!next || typeof ResizeObserver === "undefined") return;
    observer.current = new ResizeObserver(() => {
      const width = node.current?.clientWidth ?? 0;
      if (width === 0 || width === lastWidth.current) return;
      lastWidth.current = width;
      remeasure();
    });
    observer.current.observe(next);
  }, [remeasure]);
  useLayoutEffect2(() => {
    remeasure();
  }, [text, remeasure]);
  useEffect5(() => {
    const fonts = typeof document === "undefined" ? void 0 : document.fonts;
    if (!fonts) return;
    let live = true;
    fonts.ready.then(() => {
      if (live) remeasure();
    });
    return () => {
      live = false;
    };
  }, [remeasure]);
  useLayoutEffect2(() => {
    const el = node.current;
    if (!el || fontSize <= minFontSizePx) return;
    const available = el.clientWidth;
    const needed = el.scrollWidth;
    if (available === 0 || needed <= available + OVERFLOW_TOLERANCE_PX) return;
    const scaled = Math.floor(fontSize * available / needed);
    setFontSize(Math.max(minFontSizePx, Math.min(fontSize - MIN_STEP_PX, scaled)));
  });
  useEffect5(() => () => observer.current?.disconnect(), []);
  return [ref, fontSize];
}
export {
  BigButtonGroup,
  BodyCell,
  ClickableRow,
  DATA_TABLE_MOBILE_BREAKPOINT,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
  DataTableContainer,
  DataTableEmpty,
  DataTableLoading,
  DataTableMobileCards,
  DataTablePagination,
  DateRangeFilter,
  Divider_default as Divider,
  ExpandToggleCell,
  FOCUS_RING_CONTRAST_FLOOR,
  FOCUS_RING_WIDTH_PX,
  HERO_PANEL_NARROW,
  HeaderCell,
  HeroPanel_default as HeroPanel,
  IdCell,
  KeyValueEditor,
  KpiCard,
  NumericBodyCell,
  NumericHeaderCell,
  PhoneNumberField,
  PinnedActionsCell,
  RING_INK_DARK,
  RING_INK_LIGHT,
  RING_PALETTE_SLOTS,
  SecretField,
  StyledSwitch_default as StyledSwitch,
  SubOption,
  TABLE_DEFAULTS,
  chooseRingInk,
  contrastRatio,
  createDefaultDateRangePresets,
  dataTableDesktopOnlySx,
  defaultDateRangePresets,
  focusRing,
  focusRingColors,
  focusRingInkDecisions,
  focusVisibleComponents,
  formatPhoneNumberAsTyped,
  heroButtonSx,
  heroLedeSx,
  heroTitleSx,
  parsePhoneNumber,
  phoneDigits,
  relativeLuminance,
  resolveDateRangeWindow,
  resolveEffectiveWindow,
  resolveWindowFromPreset,
  useFitText,
  useOverflowFit
};
//# sourceMappingURL=index.mjs.map